/**
 * Small rules behind the Column settings panel's list of columns. They live
 * here, apart from the component, so each one can be tested on its own.
 */

/**
 * The names a relation might go by, for the stem of an id column.
 *
 * `account_manager_user_id` holds the id of `account_manager_user`. A list of
 * ids is usually named after one item, so `location_ids` may belong to
 * `locations`, and `category_ids` to `categories`.
 */
function relationNamesFor(stem: string, isList: boolean): string[] {
  if (!isList) {
    return [stem];
  }
  const names = [stem, `${stem}s`, `${stem}es`];
  if (stem.endsWith('y')) {
    names.push(`${stem.slice(0, -1)}ies`);
  }
  return names;
}

/**
 * True when a column is a raw foreign-key id that adds nothing, because the
 * readable column for the same relation is also there.
 *
 * On an account, `account_manager_user_id` shows a number like 42. The column
 * `account_manager_user` shows the person's name. So the Column settings list
 * leaves the id column out and offers only the name.
 *
 * The record's own `id` is never left out. Neither is an id column with no
 * readable partner, such as `session_id`, because then the id is all there is.
 *
 * This only decides what the list offers. It does not change the table. A
 * column someone has already shown stays listed, so they can still hide it.
 */
export function isRedundantForeignKeyColumn(
  columnId: string,
  allColumnIds: ReadonlySet<string>,
): boolean {
  if (columnId.endsWith('_ids')) {
    const stem = columnId.slice(0, -'_ids'.length);
    return stem !== '' && relationNamesFor(stem, true).some((name) => allColumnIds.has(name));
  }
  if (columnId.endsWith('_id')) {
    const stem = columnId.slice(0, -'_id'.length);
    return stem !== '' && relationNamesFor(stem, false).some((name) => allColumnIds.has(name));
  }
  return false;
}

/** 1 becomes "1st", 2 becomes "2nd", 11 becomes "11th", 22 becomes "22nd". */
export function ordinalLabel(position: number): string {
  const lastTwoDigits = position % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${position}th`;
  }
  const lastDigit = position % 10;
  if (lastDigit === 1) {
    return `${position}st`;
  }
  if (lastDigit === 2) {
    return `${position}nd`;
  }
  if (lastDigit === 3) {
    return `${position}rd`;
  }
  return `${position}th`;
}
